import React, { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { OrganizationConfiguration } from "../../models/OrganizationConfiguration/OrganizationConfiguration";
import { ImplementationType, offersCustomCaseStrategy } from "../../models/OrganizationConfiguration/ImplementationType";
import { CaseMigrationStrategy } from "../../models/OrganizationConfiguration/CaseMigrationStrategy";
import { MigrationStrategy } from "../../models/OrganizationConfiguration/MigrationStrategy";
import CaseMigrationStrategySelect from "./CaseMigrationStrategySelect";
import OutlinedDiv from "../common/OutlinedDiv";
import MigrationStrategySelect from "./MigrationStrategySelect";
import CaseNumberListControl from "./CaseNumberListControl";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import FieldError from "../common/FieldError";
import CaseFileIdsListControl from "./CaseFileIdsListControl";
import { cn } from "@/lib/utils";

export interface BigListsState {
  caseSpecifiedList: string[];
  casePriorityList: string[];
  caseIgnoredList: string[];
  caseFilesPriorityList: string[];
}

interface MigrationStrategySectionProps {
  disabled?: boolean;
  bigLists: BigListsState;
  setListField: (key: keyof BigListsState, values: string[]) => void;
}

const OrganizationFilesSynchronizationStrategySection: React.FC<MigrationStrategySectionProps> = ({
  disabled = false,
  bigLists,
  setListField,
}) => {
  const form = useFormContext<OrganizationConfiguration>();

  const casesMigrationStrategy = form.watch("casesMigrationStrategy");
  const migrateOpenedCases = form.watch("migrateOpenedCases");
  const migrateClosedCases = form.watch("migrateClosedCases");
  const casesOpenedAfter = form.watch("casesOpenedAfter");
  const casesClosedAfter = form.watch("casesClosedAfter");
  const casesOpenedBefore = form.watch("casesOpenedBefore");
  const casesClosedBefore = form.watch("casesClosedBefore");
  const implementationType = form.watch("implementationType");
  const providersMigrationStrategy = form.watch("providersMigrationStrategy");
  const firmDocumentsMigrationStrategy = form.watch("firmDocumentsMigrationStrategy");
  const templateFilesMigrationStrategy = form.watch("templateFilesMigrationStrategy");

  const handleMigrationStrategyChange = (
    strategy: MigrationStrategy | "",
    property: keyof OrganizationConfiguration
  ) => {
    form.setValue(property as any, strategy);
  };

  const offersCustom = offersCustomCaseStrategy(implementationType);
  const showCustomSettings = offersCustom
    && Array.isArray(casesMigrationStrategy)
    && casesMigrationStrategy.includes(CaseMigrationStrategy.Custom);

  // Under Custom a list replaces the open/closed windows: with one, only the listed cases migrate, whatever their dates.
  const showSpecifiedCaseNumbers = Array.isArray(casesMigrationStrategy)
    && (casesMigrationStrategy.includes(CaseMigrationStrategy.AllCases)
      || casesMigrationStrategy.includes(CaseMigrationStrategy.Custom));

  const isTrialworks = implementationType === ImplementationType.TrialWorks;
  const isSourceApi = implementationType === ImplementationType.Filevine
    || implementationType === ImplementationType.Dropbox;
  const isFilevine = implementationType === ImplementationType.Filevine;

  // Stable callbacks so memoized list controls don't re-render on parent ticks.
  const onSpecifiedChange = useCallback(
    (v: string[]) => setListField("caseSpecifiedList", v),
    [setListField],
  );
  const onPriorityChange = useCallback(
    (v: string[]) => setListField("casePriorityList", v),
    [setListField],
  );
  const onIgnoredChange = useCallback(
    (v: string[]) => setListField("caseIgnoredList", v),
    [setListField],
  );
  const onFilesPriorityChange = useCallback(
    (v: string[]) => setListField("caseFilesPriorityList", v),
    [setListField],
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Cases Strategy */}
      <OutlinedDiv label="Cases Synchronization Strategy">
        <CaseMigrationStrategySelect
          value={
            casesMigrationStrategy || [
              CaseMigrationStrategy.AllCases,
              CaseMigrationStrategy.DateDescending,
            ]
          }
          onChange={(strategies) => form.setValue("casesMigrationStrategy", strategies)}
          disabled={disabled}
          hideCustom={!offersCustom}
        />

        {showCustomSettings && (
          <div className="flex flex-col gap-3 mt-3">
            <div className={cn("flex items-center gap-3 flex-wrap", disabled && "opacity-50 pointer-events-none")}>
              <Switch
                checked={migrateOpenedCases}
                onCheckedChange={(isChecked) => {
                  form.setValue("migrateOpenedCases", isChecked);
                  if (!isChecked) {
                    form.setValue("casesOpenedAfter", null);
                    form.setValue("casesOpenedBefore", null);
                  }
                }}
              />
              <Label className="w-16 shrink-0">Open</Label>
              <DatePicker
                disabled={!migrateOpenedCases}
                placeholder="Opened after"
                value={casesOpenedAfter}
                onChange={(value) => form.setValue("casesOpenedAfter", value)}
              />
              <Label className={cn("w-28 shrink-0 text-muted-foreground text-xs", !migrateOpenedCases && "opacity-50")}>
                Opened after
              </Label>
              <DatePicker
                disabled={!migrateOpenedCases}
                placeholder="Opened before"
                value={casesOpenedBefore}
                onChange={(value) => form.setValue("casesOpenedBefore", value)}
              />
              <Label className={cn("w-28 shrink-0 text-muted-foreground text-xs", !migrateOpenedCases && "opacity-50")}>
                Opened before
              </Label>
            </div>
            <FieldError name="casesOpenedBefore" className="ml-12 text-xs" />

            <div className={cn(
              "flex items-center gap-3 flex-wrap",
              (isFilevine || disabled) && "opacity-50 pointer-events-none"
            )}>
              <Switch
                checked={migrateClosedCases}
                onCheckedChange={(isChecked) => {
                  form.setValue("migrateClosedCases", isChecked);
                  if (!isChecked) {
                    form.setValue("casesClosedAfter", null);
                    form.setValue("casesClosedBefore", null);
                  }
                }}
              />
              <Label className="w-16 shrink-0">Closed</Label>
              <DatePicker
                disabled={!migrateClosedCases}
                placeholder="Closed after"
                value={casesClosedAfter}
                onChange={(value) => form.setValue("casesClosedAfter", value)}
              />
              <Label className={cn("w-28 shrink-0 text-muted-foreground text-xs", !migrateClosedCases && "opacity-50")}>
                Closed after
              </Label>
              <DatePicker
                disabled={!migrateClosedCases}
                placeholder="Closed before"
                value={casesClosedBefore}
                onChange={(value) => form.setValue("casesClosedBefore", value)}
              />
              <Label className={cn("w-28 shrink-0 text-muted-foreground text-xs", !migrateClosedCases && "opacity-50")}>
                Closed before
              </Label>
            </div>
            <FieldError name="casesClosedBefore" className="ml-12 text-xs" />
          </div>
        )}

        {showSpecifiedCaseNumbers && (
          <CaseNumberListControl
            disabled={disabled}
            label="Specified case numbers"
            values={bigLists.caseSpecifiedList}
            onChange={onSpecifiedChange}
            isTrialworks={isTrialworks}
            isSourceApi={isSourceApi}
          />
        )}

        <CaseNumberListControl
          disabled={disabled}
          label="Prioritized case numbers"
          values={bigLists.casePriorityList}
          onChange={onPriorityChange}
          isTrialworks={isTrialworks}
          isSourceApi={isSourceApi}
        />
        <CaseNumberListControl
          disabled={disabled}
          label="Ignored case numbers"
          values={bigLists.caseIgnoredList}
          onChange={onIgnoredChange}
          isTrialworks={isTrialworks}
          isSourceApi={isSourceApi}
          helpText="Cases listed here are fully excluded from discovery and migration regardless of the synchronization strategy."
        />
        <CaseFileIdsListControl
          disabled={disabled}
          label="Prioritized case file ids"
          values={bigLists.caseFilesPriorityList}
          onChange={onFilesPriorityChange}
        />
      </OutlinedDiv>

      {/* Other Strategies — compact grid */}
      <div className="grid grid-cols-3 gap-3">
        <MigrationStrategySelect
          value={providersMigrationStrategy || MigrationStrategy.DateDescending}
          onChange={(value) => handleMigrationStrategyChange(value, "providersMigrationStrategy")}
          label="Providers Strategy"
          required
          disabled={isFilevine || disabled}
        />
        <MigrationStrategySelect
          value={firmDocumentsMigrationStrategy || MigrationStrategy.DateDescending}
          onChange={(value) => handleMigrationStrategyChange(value, "firmDocumentsMigrationStrategy")}
          label="Firm Documents Strategy"
          required
          disabled={isFilevine || disabled}
        />
        <MigrationStrategySelect
          value={templateFilesMigrationStrategy || MigrationStrategy.DateDescending}
          onChange={(value) => handleMigrationStrategyChange(value, "templateFilesMigrationStrategy")}
          label="Template Files Strategy"
          required
          disabled={isFilevine || disabled}
        />
      </div>
    </div>
  );
};

export default OrganizationFilesSynchronizationStrategySection;
